import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export type CaseCustomerType =
  | 'AFFILIATE'
  | 'DIRECT_ONRAMP'
  | 'APP'
  | 'MERCHANT';

export interface Note {
  createdAt: string;
  content: string;
  createdBy: string;
}

export interface CaseDetailsResponse {
  notes: Note[];
}

const queryKeys = {
  all: () => ['fcu', 'customer-notes'] as const,
  list: (customerId?: string) =>
    [...queryKeys.all(), 'list', customerId] as const,
};

export const useGetCustomerNotes = (customerId: string) => {
  return useQuery<CaseDetailsResponse, ErrorResponse>({
    queryKey: queryKeys.list(customerId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customer_id', customerId);
      const queryString = params.toString();
      return await fetchAPI<CaseDetailsResponse>(
        `v1/fcu/customer-notes?${queryString}`
      );
    },
    enabled: !!customerId,
    placeholderData: keepPreviousData,
  });
};

export const useAddCustomerNote = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    { uuid: string },
    ErrorResponse,
    { content: string; customerId: string; customerType: string }
  >({
    mutationFn: async ({ content, customerId, customerType }) => {
      return await fetchAPI<{ uuid: string }>(`v1/fcu/customer-notes`, {
        method: 'POST',
        body: { content, customerId, customerType },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.list(customerId),
      });
    },
  });
};
